import { useLanguage } from "@/contexts/LanguageContext";
import {
  CalendarDays, MapPin, Phone, Mail, MessageCircle, ExternalLink, QrCode, Navigation, Building2,
} from "lucide-react";
import { loc } from "@/lib/locale";

const waLink = (v) => `https://wa.me/${String(v).replace(/[^\d]/g, "")}`;

const BTN =
  "inline-flex items-center justify-center gap-2 rounded-lg font-bold text-xs uppercase tracking-wider px-4 py-3 transition-colors w-full sm:w-auto";

export default function BookingSection({ attractions }) {
  const { language } = useLanguage();
  const pt = language !== "en";
  if (!attractions || attractions.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="booking-section">
      {/* Global notice */}
      <div className="rounded-2xl border border-gold/50 bg-[#fdf8ee] p-5 shadow-sm" data-testid="booking-alert">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-navy flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg text-navy font-bold">
              {pt ? "Agendamento necessário" : "Booking required"}
            </h3>
            <p className="text-navy/75 text-sm mt-1 leading-relaxed">
              {pt
                ? "O seu PASS inclui uma ou mais experiências que requerem agendamento prévio de data e horário. Consulte abaixo as informações de cada experiência e efetue o agendamento com antecedência."
                : "Your PASS includes one or more experiences that require prior booking of date and time. See the information for each experience below and book in advance."}
            </p>
            <p className="text-navy/60 text-xs mt-2 leading-relaxed border-t border-gold/25 pt-2">
              <strong className="text-navy/80 uppercase tracking-wide">{pt ? "Importante:" : "Important:"}</strong>{" "}
              {pt
                ? "A compra do PASS não constitui uma reserva automática para uma data ou horário específico. As reservas estão sujeitas à disponibilidade, capacidade, horários e condições operacionais do respetivo parceiro."
                : "Purchasing the PASS does not constitute an automatic reservation for a specific date or time. Reservations are subject to the partner's availability, capacity, schedules and operating conditions."}
            </p>
          </div>
        </div>
      </div>

      {/* Per-experience booking cards */}
      {attractions.map((a) => {
        const b = a.booking || {};
        const presencial = (b.methods || []).includes("presencial");
        return (
          <div
            key={a.id}
            className="rounded-2xl border border-gold/25 bg-white p-5 shadow-sm"
            data-testid={`booking-card-${a.id}`}
          >
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h4 className="font-heading text-navy font-bold text-base">{loc(a, "name", language)}</h4>
              <span className="inline-flex items-center gap-1 bg-navy text-gold text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                <CalendarDays className="w-3 h-3" />
                {pt ? "Agendamento obrigatório" : "Booking required"}
              </span>
            </div>

            <p className="text-navy/70 text-sm leading-relaxed">
              {pt
                ? "Para utilizar esta experiência, efetue previamente o agendamento da data e horário junto do parceiro."
                : "To use this experience, book the date and time with the partner in advance."}
            </p>

            {presencial && (
              <div className="mt-3 rounded-lg bg-navy/[0.04] border border-navy/10 px-4 py-3">
                <p className="text-navy text-sm font-semibold flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                  {pt
                    ? "Dirija-se à agência indicada abaixo para efetuar o agendamento da data e horário do seu passeio."
                    : "Please visit the agency indicated below to book the date and time of your tour."}
                </p>
                <p className="text-navy/60 text-xs mt-1.5 pl-6">
                  {pt
                    ? "Recomendamos efetuar o agendamento com antecedência para aumentar a disponibilidade de datas e horários."
                    : "We recommend booking in advance to increase the availability of dates and times."}
                </p>
              </div>
            )}

            {(b.agency_name || b.address) && (
              <div className="mt-3 space-y-1.5">
                {b.agency_name && (
                  <p className="text-sm text-navy/80">
                    <span className="text-navy/50 text-xs uppercase tracking-wider block">{pt ? "Local de agendamento:" : "Booking location:"}</span>
                    <span className="font-semibold text-navy">{b.agency_name}</span>
                  </p>
                )}
                {b.address && (
                  <p className="text-sm text-navy/80">
                    <span className="text-navy/50 text-xs uppercase tracking-wider block">{pt ? "Endereço:" : "Address:"}</span>
                    <span className="font-semibold text-navy flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" /> {b.address}</span>
                  </p>
                )}
              </div>
            )}

            {b.instructions && (
              <p className="mt-3 text-navy/70 text-sm leading-relaxed bg-gold/5 border border-gold/20 rounded-lg px-3 py-2">
                {b.instructions}
              </p>
            )}

            <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              {b.maps_url && (
                <a href={b.maps_url} target="_blank" rel="noreferrer" className={`${BTN} bg-navy text-gold hover:bg-navy/90`} data-testid={`booking-maps-${a.id}`}>
                  <Navigation className="w-4 h-4" /> {pt ? "Como chegar" : "Get directions"}
                </a>
              )}
              {b.phone && (
                <a href={`tel:${b.phone}`} className={`${BTN} bg-gold text-navy hover:bg-gold-dark hover:text-white`} data-testid={`booking-phone-${a.id}`}>
                  <Phone className="w-4 h-4" /> {pt ? "Ligar" : "Call"}
                </a>
              )}
              {b.whatsapp && (
                <a href={waLink(b.whatsapp)} target="_blank" rel="noreferrer" className={`${BTN} bg-emerald-600 text-white hover:bg-emerald-700`} data-testid={`booking-whatsapp-${a.id}`}>
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {b.email && (
                <a href={`mailto:${b.email}`} className={`${BTN} border border-navy/25 text-navy hover:bg-navy/5`} data-testid={`booking-email-${a.id}`}>
                  <Mail className="w-4 h-4" /> {pt ? "Enviar e-mail" : "Send email"}
                </a>
              )}
              {b.external_url && (
                <a href={b.external_url} target="_blank" rel="noreferrer" className={`${BTN} border border-gold text-navy hover:bg-gold/10`} data-testid={`booking-external-${a.id}`}>
                  <ExternalLink className="w-4 h-4" /> {pt ? "Reservar online" : "Book online"}
                </a>
              )}
            </div>

            <p className="mt-4 text-navy/60 text-xs flex items-center gap-1.5 border-t border-navy/10 pt-3">
              <QrCode className="w-3.5 h-3.5 text-gold flex-shrink-0" />
              {pt
                ? "No dia da experiência, apresente o seu QR Code PASS Algarve para validação junto do parceiro."
                : "On the day of the experience, present your PASS Algarve QR Code to the partner for validation."}
            </p>
          </div>
        );
      })}
    </div>
  );
}
